import React from 'react'

import { useCampaignDebouncedState } from '@/app/module/campaigns/component/hooks/use-campaign-debounced-state'
import { ENABLED_RETRIES } from '@/app/module/campaigns/definitions'
import {
  CampaignTemplateType,
  LocalOnInvalidReply,
  LocalOnRetriesExhausted,
  LocalOnTimeout,
} from '@/app/module/campaigns/types'

import { ActionProps, ApiProps } from './part-config-props'
import ResponseConfigRetries from './response-config-retries'
import { ConfigDivider } from './response-configs-components'
import { fromConfigs, SmsConfigs, SmsConfigsState, toConfigs } from './response-sms-configs-utils'
import ResponseSmsFailure from './response-sms-failure'
import ResponseSmsInvalid from './response-sms-invalid'
import ResponseSmsTimeout from './response-sms-timeout'

type Props = {
  actionProps: ActionProps
  apiProps: ApiProps
  campaignType: CampaignTemplateType
  configs: SmsConfigs
  id: string

  onChange: (value: SmsConfigs) => void
}

const ResponseSmsConfigs: React.FC<Props> = ({ actionProps, apiProps, campaignType, configs, id, onChange }) => {
  const initialState = React.useMemo(() => fromConfigs(configs), [configs])
  const handleChange = React.useCallback((value: SmsConfigsState) => onChange(toConfigs(value)), [onChange])
  const [state, setState] = useCampaignDebouncedState(initialState, handleChange)

  const handleInvalidChange = React.useCallback(
    (value: Partial<LocalOnInvalidReply>) =>
      setState((s) => {
        if (!s.onInvalidReply) return s

        const shouldUpdateRetries = 'enabled' in value
        if (!shouldUpdateRetries) return { ...s, onInvalidReply: { ...s.onInvalidReply, ...value } }

        const defaultRetries = s.retries || ENABLED_RETRIES
        return {
          ...s,
          onInvalidReply: { ...s.onInvalidReply, ...value },
          retries: value.enabled || s.onTimeout.enabled ? defaultRetries : 0,
        }
      }),
    [setState],
  )
  const handleTimeoutChange = React.useCallback(
    (value: Partial<LocalOnTimeout>) =>
      setState((s) => {
        const shouldUpdateRetries = 'enabled' in value
        if (!shouldUpdateRetries) return { ...s, onTimeout: { ...s.onTimeout, ...value } }
        const defaultRetries = s.retries || ENABLED_RETRIES
        return {
          ...s,
          onTimeout: { ...s.onTimeout, ...value },
          retries: value.enabled || s.onInvalidReply?.enabled ? defaultRetries : 0,
        }
      }),
    [setState],
  )
  const handleRetriesChange = React.useCallback(
    ({ value }: { value: string }) => setState((s) => ({ ...s, retries: Number(value) })),
    [setState],
  )
  const handleFailureChange = React.useCallback(
    (value: Partial<LocalOnRetriesExhausted>) =>
      setState((s) => ({ ...s, onRetriesExhausted: { ...s.onRetriesExhausted, ...value } })),
    [setState],
  )

  const retriesEnabled = !!(state.onInvalidReply?.enabled || state.onTimeout.enabled)

  return (
    <>
      {!!state.onInvalidReply && (
        <>
          <ConfigDivider />
          <ResponseSmsInvalid
            actionProps={actionProps}
            apiProps={apiProps}
            campaignType={campaignType}
            id={id}
            info="Specify what we should do if the contact's reply is not one of the choices. Afterwards, we will wait for the contact to reply with a valid choice—until they run out of chances."
            invalid={state.onInvalidReply}
            onChange={handleInvalidChange}
          />
        </>
      )}
      <ConfigDivider />
      <ResponseSmsTimeout
        actionProps={actionProps}
        apiProps={apiProps}
        campaignType={campaignType}
        id={id}
        info="Specify what we should do if the contact does not reply within a period of time that you specify. Afterwards, we will wait for the contact to reply—until they run out of chances."
        inputBeforeText="Take the above actions if the contact does not reply to this question after: "
        timeout={state.onTimeout}
        onChange={handleTimeoutChange}
      />
      <ConfigDivider />
      <div className="campaign-question-failure-retries">
        <ResponseConfigRetries retries={state.retries} retriesEnabled={retriesEnabled} onChange={handleRetriesChange} />
      </div>
      <ConfigDivider />
      <ResponseSmsFailure
        actionProps={actionProps}
        apiProps={apiProps}
        campaignType={campaignType}
        failure={state.onRetriesExhausted}
        id={id}
        info="Specify what we should do after the contact runs out of chances."
        onChange={handleFailureChange}
      />
    </>
  )
}

export default React.memo(ResponseSmsConfigs)

export type { Props as ResponseSmsConfigsProps }
